<template>
    <div class="ex-card" :class="{ 'ex-card--active': active }" @click="$emit('activate')">
        <!-- 標題列 -->
        <div class="ex-head">
            <span class="ex-no">{{ $t('unit.frame.sample_n', { n: index + 1 }) }}</span>
            <span v-if="isBlank" class="ex-todo">{{ $t('unit.frame.state_todo') }}</span>
            <span v-if="fieldCount >= FIELD_WARN" class="ex-count">
                {{ fieldCount >= FIELD_MAX
                    ? $t('unit.field.max_reached')
                    : $t('unit.field.count', { n: fieldCount, max: FIELD_MAX }) }}
            </span>
            <span class="ex-spacer"></span>
            <button type="button" class="ex-del" @click.stop="$emit('delete')">
                {{ $t('button.delete') }}
            </button>
        </div>

        <!--
            句子框：**每個文字段是一個獨立的透明 input，欄位是 button**。
            刻意不用 contenteditable——選字、游標、IME 全部交給原生 input 處理，
            不必自己接管 selection range（設計稿 v4 的做法，也是 F6 當時標的「退路」）。
        -->
        <div class="ex-sentence" @click="onSentenceClick">
            <template v-for="(tk, ti) in tokens">
                <input v-if="tk.kind === 'text'" :key="tk.id"
                    class="tk-text" type="text"
                    :value="tk.v"
                    :style="{ width: textWidth(tk.v) }"
                    :ref="`tk${tk.id}`"
                    @input="onTextInput(ti, $event)"
                    @select="onTextSelect(ti, $event)"
                    @click="onTextSelect(ti, $event)"
                    @keyup="onTextSelect(ti, $event)"
                    @keydown="onTextKeydown(ti, $event)">
                <button v-else :key="tk.id" type="button"
                    class="tk-field"
                    :class="{
                        'tk-field--on': tk.id === selectedFieldId,
                        'tk-field--dup': dupNames.includes(tk.name),
                    }"
                    @click.stop="selectField(tk.id)">{{ tk.name }}</button>
            </template>
        </div>

        <!-- 操作列只在當前卡顯示；其他卡仍可直接改字 -->
        <div v-if="active" class="ex-ops">
            <button type="button" class="btn-outline"
                :disabled="fieldCount >= FIELD_MAX"
                @click.stop="makeField">
                {{ hasSelection
                    ? $t('unit.field.set_from_selection')
                    : $t('unit.field.insert_at_cursor') }}
            </button>
        </div>

        <!-- 欄位設定面板：就地展開在這張卡內 -->
        <div v-if="selectedField" class="fp" @click.stop>
            <button type="button" class="fp-close" @click="removeField">×</button>

            <div class="fp-row">
                <div class="fp-block">
                    <div class="fp-label">{{ $t('unit.field.name_label') }}</div>
                    <input class="fp-name" type="text"
                        :class="{ 'fp-name--err': !!nameError }"
                        :value="selectedField.name"
                        @input="onNameInput">
                    <div v-if="nameError" class="fp-err">{{ nameError }}</div>
                </div>

                <div class="fp-block">
                    <div class="fp-label">{{ $t('unit.field.mode_label') }}</div>
                    <div class="seg">
                        <button type="button" class="seg-btn"
                            :class="{ 'seg-btn--on': selectedField.mode === 'free' }"
                            @click="setMode('free')">{{ $t('unit.field.mode_free') }}</button>
                        <button type="button" class="seg-btn"
                            :class="{ 'seg-btn--on': selectedField.mode === 'options' }"
                            @click="setMode('options')">{{ $t('unit.field.mode_options') }}</button>
                    </div>
                </div>

                <div v-if="selectedField.mode === 'options'" class="fp-block fp-block--wide">
                    <div class="fp-label">{{ $t('unit.field.options_label') }}</div>
                    <div class="opt-grid">
                        <div v-for="(op, oi) in selectedField.options" :key="oi" class="opt-row">
                            <input class="opt-input" type="text" :value="op"
                                @input="setOption(oi, $event.target.value)">
                            <button type="button" class="opt-del" @click="removeOption(oi)">×</button>
                        </div>
                        <input v-if="selectedField.options.length < OPTION_MAX"
                            class="opt-new" type="text"
                            :value="newOpt"
                            :placeholder="$t('unit.field.options_placeholder')"
                            @input="newOpt = $event.target.value"
                            @keydown.enter.prevent="addOption($event)">
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const FIELD_MAX = 6;
const FIELD_WARN = 5;
const OPTION_MAX = 6;
const NAME_MAX = 12;
const OPTION_LEN_MAX = 20;

let tokenSeed = 0;

/**
 * 一張句型範例卡（設計稿 v4 的「範例卡」）。
 *
 * ⚠ 句子框**不是 contenteditable**：每個文字段是一個透明 `<input>`、欄位是 `<button>`。
 * 選字、游標、IME 全部交給原生 input，不必自己接管 selection range。
 * F6 曾驗證過 contenteditable 的原子標記在桌面可行，但當時就判斷「退路在觸控上更穩健」，
 * v4 設計獨立走到同一個方向。
 *
 * 資料仍然存成 `{ content: "今天我對(家人)說話", option: [{field, options}] }`——
 * token 只是編輯期的表示法。`mode` **不持久化**：`free` 與「還沒給選項」存進 DB
 * 都是 `options: []`，靠前端的 mode 區分意圖（2026-08-10 決定）。
 */
export default {
    name: 'ExampleCard',
    props: {
        content: { type: String, default: '' },
        option: { type: Array, default: () => [] },
        index: { type: Number, default: 0 },
        active: { type: Boolean, default: false },
    },
    data() {
        return {
            tokens: [],
            selectedFieldId: null,
            /// 句子框內的選取範圍或游標位置，決定「設為替換欄位」的行為與文案
            textSel: null,
            newOpt: '',
            nameError: '',
            /// 量測要等 $el 掛上才做得了；翻成 true 時順便觸發一次重繪把寬度補正
            measureReady: false,
            FIELD_MAX,
            FIELD_WARN,
            OPTION_MAX,
        };
    },
    computed: {
        selectedField() {
            return this.tokens.find(
                (t) => t.kind === 'field' && t.id === this.selectedFieldId) || null;
        },
        fieldTokens() {
            return this.tokens.filter((t) => t.kind === 'field');
        },
        fieldCount() {
            return this.fieldTokens.length;
        },
        /// 同一個範例內不可重複（brief §5）
        dupNames() {
            const seen = [];
            const dup = [];
            this.fieldTokens.forEach((t) => {
                if (seen.includes(t.name)) {
                    dup.push(t.name);
                } else {
                    seen.push(t.name);
                }
            });
            return dup;
        },
        isBlank() {
            return !this.tokens.some((t) => t.kind === 'text' && t.v.trim());
        },
        hasSelection() {
            return !!this.textSel && this.textSel.start !== this.textSel.end;
        },
    },
    watch: {
        /// 學生端示意要把當前欄位加深，所以選取狀態要往上送
        selectedField(field) {
            this.$emit('field-select', field ? field.name : null);
        },
    },
    created() {
        this.parse();
    },
    mounted() {
        this.measureReady = true;
    },
    methods: {
        /* ---------- content + option ⇄ tokens ---------- */

        /** 依 spec §3 還原：只認 option[] 列出的欄位名，不做通用括號解析 */
        parse() {
            const fields = (this.option || []).map((o) => ({
                name: o.field,
                options: Array.isArray(o.options) ? o.options.slice() : [],
                /// 0 個選項一律視為刻意的自由填寫——那是老師存過的狀態
                mode: Array.isArray(o.options) && o.options.length ? 'options' : 'free',
            }));
            const out = [];
            const used = new Set();
            let rest = this.content || '';
            let guard = 0;
            while (guard++ < 200) {
                let at = -1;
                let hit = null;
                fields.forEach((f) => {
                    if (used.has(f.name)) {
                        return;
                    }
                    const i = rest.indexOf(`(${f.name})`);
                    if (i !== -1 && (at === -1 || i < at)) {
                        at = i;
                        hit = f;
                    }
                });
                if (at === -1) {
                    break;
                }
                out.push(this.textToken(rest.slice(0, at)));
                out.push(this.fieldToken(hit.name, hit.mode, hit.options));
                used.add(hit.name);
                rest = rest.slice(at + hit.name.length + 2);
            }
            out.push(this.textToken(rest));
            this.tokens = out;
        },

        textToken(v) {
            tokenSeed += 1;
            return { id: `t${tokenSeed}`, kind: 'text', v: v || '' };
        },
        fieldToken(name, mode, options) {
            tokenSeed += 1;
            return {
                id: `f${tokenSeed}`,
                kind: 'field',
                name,
                mode: mode || 'free',
                options: options || [],
            };
        },

        serialize() {
            let content = '';
            const option = [];
            this.tokens.forEach((t) => {
                if (t.kind === 'text') {
                    content += t.v;
                    return;
                }
                content += `(${t.name})`;
                option.push({
                    field: t.name,
                    /// mode 不持久化：free 與「還沒給選項」都是空陣列
                    options: t.mode === 'options' ? t.options.slice() : [],
                });
            });
            return { content, option };
        },

        emitChange() {
            const { content, option } = this.serialize();
            this.$emit('change', {
                content,
                option,
                /// 會讓資料壞掉的兩種——存了再載入欄位位置會靜默前移，必須擋（2026-08-10）
                blocking: this.blockingError(content),
                /// 只是還沒填完，不擋
                unprocessed: this.fieldTokens.filter(
                    (t) => t.mode === 'options' && t.options.length === 0).length,
            });
        },

        blockingError(content) {
            if (this.dupNames.length) {
                return this.$t('unit.validation.field_name_duplicate', { name: this.dupNames[0] });
            }
            if (this.fieldTokens.some((t) => !t.name.trim())) {
                return this.$t('unit.validation.field_name_required');
            }
            const literal = this.fieldTokens.find(
                (t) => content.split(`(${t.name})`).length - 1 > 1);
            return literal
                ? this.$t('unit.validation.field_name_literal_conflict', { name: literal.name })
                : null;
        },

        /* ---------- 句子框 ---------- */

        /**
         * 文字 token 的寬度。
         *
         * 原本照設計稿用 `max(26, 字數 × 17 + 14)`，但那有兩個問題：
         * 1. **空的文字 token 也佔 26px**，而 `parse()` 在每個欄位前後都會放一個
         *    （句首就是欄位、或兩個欄位相鄰時就是空的）→ 欄位左邊出現一大塊空白。
         * 2. 每個字都算 17px 是中文的寬度，英數字會被高估一倍以上。
         *
         * 改成用 canvas 實測。空字串只留 8px，還按得到、也不會撐出空隙。
         */
        textWidth(v) {
            /// 先讀一次建立相依，mounted 後才會重算寬度
            const ready = this.measureReady;
            if (!v) {
                /// ⚠ 整句還是空的時候（剛新增的範例），唯一的那個文字 token 必須夠寬
                /// 才打得進字——8px 是給「兩個欄位之間的空隙」用的，不是給空白句子用的。
                return this.tokens.length === 1 ? '100%' : '8px';
            }
            const ctx = ready ? this.measureCtx() : null;
            if (!ctx) {
                return `${v.length * 17 + 4}px`;
            }
            /// +3 是留給游標的位置，不然打到結尾時字會被推著捲動
            return `${Math.ceil(ctx.measureText(v).width) + 3}px`;
        },

        measureCtx() {
            if (this.measureCache) {
                return this.measureCache;
            }
            const box = this.$el && this.$el.querySelector('.ex-sentence');
            if (!box || typeof document === 'undefined') {
                return null;
            }
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                return null;
            }
            const cs = window.getComputedStyle(box);
            ctx.font = `${cs.fontStyle} ${cs.fontWeight} 16px ${cs.fontFamily}`;
            this.measureCache = ctx;
            return ctx;
        },

        onTextInput(ti, e) {
            this.tokens[ti].v = e.target.value;
            this.recordSel(ti, e.target);
            this.emitChange();
        },

        onTextSelect(ti, e) {
            this.recordSel(ti, e.target);
        },

        /**
         * 原生 input 的游標與刪除都停在自己的邊界，跨不到隔壁的 token，
         * 所以 Backspace 與左右方向鍵都得自己接。
         */
        onTextKeydown(ti, e) {
            const el = e.target;
            const collapsed = el.selectionStart === el.selectionEnd;
            const atStart = collapsed && el.selectionStart === 0;
            const atEnd = collapsed && el.selectionStart === el.value.length;

            if (e.key === 'Backspace' && atStart) {
                const prev = this.tokens[ti - 1];
                if (prev && prev.kind === 'field') {
                    e.preventDefault();
                    this.removeTokenAt(ti - 1, false);
                }
                return;
            }
            /// token 是「文字／欄位／文字」交錯的，所以隔壁的文字在 ±2
            if (e.key === 'ArrowLeft' && atStart && this.focusText(ti - 2, 'end')) {
                e.preventDefault();
                return;
            }
            if (e.key === 'ArrowRight' && atEnd && this.focusText(ti + 2, 'start')) {
                e.preventDefault();
            }
        },

        /**
         * 點句子框的空白處 → 把游標帶到最後一段文字。
         *
         * 只在點到框本身（padding／換行後的空白）時才處理；點在 input 或欄位按鈕上
         * 由它們自己接手，否則會把游標從老師剛點的位置搶走。
         */
        onSentenceClick(event) {
            if (event.target !== event.currentTarget) {
                return;
            }
            for (let i = this.tokens.length - 1; i >= 0; i -= 1) {
                if (this.tokens[i].kind === 'text') {
                    this.focusText(i, 'end');
                    return;
                }
            }
        },

        /** 把游標移到第 idx 個文字 token；沒有這個 token 就回 false，讓原生行為接手 */
        focusText(idx, caret) {
            const tk = this.tokens[idx];
            if (!tk || tk.kind !== 'text') {
                return false;
            }
            this.$nextTick(() => {
                const ref = this.$refs[`tk${tk.id}`];
                const el = Array.isArray(ref) ? ref[0] : ref;
                if (!el) {
                    return;
                }
                el.focus();
                const at = caret === 'end' ? el.value.length : 0;
                el.setSelectionRange(at, at);
                this.recordSel(idx, el);
            });
            return true;
        },

        recordSel(ti, el) {
            this.textSel = {
                tokenIdx: ti,
                start: el.selectionStart,
                end: el.selectionEnd,
                text: (this.tokens[ti].v || '').slice(el.selectionStart, el.selectionEnd),
            };
        },

        /**
         * 加入替換欄位。有選取 → 把該段切成 文字/欄位/文字；
         * 無選取 → 在游標處切開插入；游標不在句子內 → 附加到句尾。
         */
        makeField() {
            if (this.fieldCount >= FIELD_MAX) {
                return;
            }
            const sel = this.textSel;
            if (!sel || this.tokens[sel.tokenIdx]?.kind !== 'text') {
                this.tokens.push(this.fieldToken(this.nextName(), 'free', []));
                this.tokens.push(this.textToken(''));
                this.afterMakeField();
                return;
            }
            const tk = this.tokens[sel.tokenIdx];
            const before = tk.v.slice(0, sel.start);
            const picked = tk.v.slice(sel.start, sel.end);
            const after = tk.v.slice(sel.end);
            const name = this.nextName(picked.trim());
            tk.v = before;
            this.tokens.splice(sel.tokenIdx + 1, 0,
                this.fieldToken(name, 'free', []),
                this.textToken(after));
            this.afterMakeField();
        },

        afterMakeField() {
            const last = this.fieldTokens[this.fieldTokens.length - 1];
            this.selectedFieldId = last ? last.id : null;
            this.textSel = null;
            this.nameError = '';
            this.emitChange();
        },

        /** 名稱預設為選取的文字；撞名自動加序號（brief §5） */
        nextName(base) {
            const raw = (base || this.$t('unit.field.default_base')).slice(0, NAME_MAX);
            const taken = this.fieldTokens.map((t) => t.name);
            if (!taken.includes(raw)) {
                return raw;
            }
            let i = 2;
            while (taken.includes(`${raw}${i}`)) {
                i += 1;
            }
            return `${raw}${i}`;
        },

        selectField(id) {
            this.selectedFieldId = this.selectedFieldId === id ? null : id;
            this.nameError = '';
            this.newOpt = '';
            this.$emit('activate');
        },

        /* ---------- 欄位設定面板 ---------- */

        onNameInput(e) {
            const raw = e.target.value.slice(0, NAME_MAX);
            const field = this.selectedField;
            if (!field) {
                return;
            }
            field.name = raw;
            /// 即時驗證，不等到儲存（設計稿 §7）
            if (!raw.trim()) {
                this.nameError = this.$t('unit.validation.field_name_required');
            } else if (this.fieldTokens.filter((t) => t.name === raw).length > 1) {
                this.nameError = this.$t('unit.validation.field_name_duplicate', { name: raw });
            } else {
                this.nameError = '';
            }
            this.emitChange();
        },

        setMode(mode) {
            const field = this.selectedField;
            if (!field || field.mode === mode) {
                return;
            }
            field.mode = mode;
            /// 切到「從選項中選」時從空開始，不預先塞空白列（設計稿）
            if (mode === 'options') {
                field.options = [];
            }
            this.newOpt = '';
            this.emitChange();
        },

        /**
         * 按 Enter 新增一個選項，清空輸入框、保留焦點，可以連續輸入。
         *
         * ⚠ **組字中的 Enter 要忽略。** 中文輸入法用 Enter 確認選字，這時
         * `isComposing` 為 true（Safari 甚至照樣回報 keyCode 13）。若照做，
         * `newOpt` 還沒拿到最終選字就被清空，輸入法接著才把字送進來——
         * 畫面上看起來就是「按了 Enter 沒有清空」。
         */
        addOption(event) {
            if (event && (event.isComposing || event.keyCode === 229)) {
                return;
            }
            const field = this.selectedField;
            const raw = (this.newOpt || '').trim().slice(0, OPTION_LEN_MAX);
            if (!field || !raw || field.options.length >= OPTION_MAX) {
                return;
            }
            /// 重複的詞不加入，但一樣要清空——否則老師會盯著一個按了沒反應的輸入框
            const isDuplicate = field.options.includes(raw);
            if (!isDuplicate) {
                field.options.push(raw);
            }
            this.newOpt = '';
            /// 直接清 DOM，不倚賴 re-render：`:value` 綁定在輸入框仍有焦點時
            /// 不一定會被 Vue 寫回去（updateDOMProps 的 shouldUpdateValue 判斷）
            if (event && event.target) {
                event.target.value = '';
            }
            if (!isDuplicate) {
                this.emitChange();
            }
        },

        setOption(oi, v) {
            /// ⚠ 這裡原本沒有截長度，但 addOption 有——同一個欄位「新增」會被截到 20 字、
            /// 「編輯」卻可以貼進任意長度，而後端只擋選項「個數」不擋字數。
            this.selectedField.options.splice(oi, 1, (v || '').slice(0, OPTION_LEN_MAX));
            this.emitChange();
        },

        removeOption(oi) {
            this.selectedField.options.splice(oi, 1);
            this.emitChange();
        },

        removeField() {
            const idx = this.tokens.findIndex((t) => t.id === this.selectedFieldId);
            if (idx !== -1) {
                this.removeTokenAt(idx, true);
            }
        },

        /** 移除欄位並把前後的文字 token 併回一個，避免累積空片段 */
        removeTokenAt(idx, keepName) {
            const removed = this.tokens[idx];
            const before = this.tokens[idx - 1];
            const after = this.tokens[idx + 1];
            const text = keepName ? '' : removed.name;
            if (before && after && before.kind === 'text' && after.kind === 'text') {
                before.v = before.v + text + after.v;
                this.tokens.splice(idx, 2);
            } else {
                this.tokens.splice(idx, 1, this.textToken(text));
            }
            this.selectedFieldId = null;
            this.nameError = '';
            this.emitChange();
        },
    },
};
</script>

<style scoped>
.ex-card {
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    padding: 14px 16px 16px;
    background: #fff;
}

.ex-card + .ex-card {
    margin-top: 14px;
}

.ex-card--active {
    border-color: #bdbdbd;
}

.ex-head {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
}

.ex-no {
    font-size: 13px;
    color: #757575;
}

.ex-todo {
    font-size: 12px;
    color: #ef6c00;
}

.ex-count {
    font-size: 12px;
    color: #9e9e9e;
}

.ex-spacer {
    flex: 1;
}

.ex-del {
    font-size: 13px;
    color: #d32f2f;
    background: none;
    border: 0;
    cursor: pointer;
}

.ex-sentence {
    border: 1px solid #bdbdbd;
    border-radius: 4px;
    padding: 14px 12px;
    min-height: 68px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px;
    background: #fff;
}

.tk-text {
    border: 0;
    outline: none;
    background: transparent;
    font-size: 16px;
    line-height: 1.9;
    color: #212121;
    padding: 0;
}

/* 欄位標記：中性灰膠囊、無外框、內距收窄
   （設計歷程：藍底大膠囊太搶眼、純底線又太弱，最後收斂成這樣） */
.tk-field {
    font-size: 16px;
    padding: 3px 6px;
    border-radius: 3px;
    border: 0;
    margin: 0 1px;
    background: #eceff1;
    color: #37474f;
    cursor: pointer;
}

.tk-field--on {
    background: #e3f2fd;
    color: #1565c0;
    box-shadow: inset 0 0 0 1px #64b5f6;
}

.tk-field--dup {
    background: #ffebee;
    color: #c62828;
    box-shadow: inset 0 0 0 1px #ef9a9a;
}

.ex-ops {
    margin-top: 10px;
}

.btn-outline {
    border: 1px solid #1e88e5;
    color: #1e88e5;
    background: #fff;
    border-radius: 4px;
    padding: 7px 14px;
    font-size: 13px;
    cursor: pointer;
}

.btn-outline:disabled {
    border-color: #e0e0e0;
    color: #bdbdbd;
    cursor: not-allowed;
}

.fp {
    position: relative;
    margin-top: 12px;
    background: #f7f9fb;
    border: 1px solid #dfe7ee;
    border-radius: 4px;
    padding: 14px 16px 16px;
}

.fp-close {
    position: absolute;
    top: 8px;
    right: 10px;
    font-size: 17px;
    color: #9e9e9e;
    background: none;
    border: 0;
    cursor: pointer;
    line-height: 1;
}

.fp-close:hover {
    color: #d32f2f;
}

.fp-row {
    display: flex;
    flex-wrap: wrap;
    gap: 22px;
}

.fp-label {
    font-size: 12px;
    color: #757575;
    margin-bottom: 6px;
}

.fp-name {
    width: 100%;
    max-width: 200px;
    padding: 9px 11px;
    font-size: 14px;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    outline: none;
    background: #fff;
}

.fp-name--err {
    border-color: #e53935;
}

.fp-err {
    font-size: 12px;
    color: #d32f2f;
    margin-top: 4px;
}

.seg {
    display: flex;
}

.seg-btn {
    font-size: 13px;
    padding: 8px 14px;
    border: 1px solid #e0e0e0;
    background: #fff;
    color: #616161;
    cursor: pointer;
}

.seg-btn:first-child {
    border-radius: 4px 0 0 4px;
}

.seg-btn:last-child {
    border-radius: 0 4px 4px 0;
    margin-left: -1px;
}

.seg-btn--on {
    background: #e3f2fd;
    color: #1565c0;
    border-color: #64b5f6;
    z-index: 1;
}

.fp-block--wide {
    /* ⚠ 不能寫 `flex: 1` + `min-width: 260px`。`flex: 1` 的 basis 是 0，換行演算法
       會以為這一塊不佔空間而不換行，等到排版時 min-width 又把它撐到 260px——
       結果就是超出面板。basis 直接給 260px，換行才算得準。 */
    flex: 1 1 260px;
    min-width: 0;
}

.opt-grid {
    display: grid;
    /* auto-fit + minmax：窄的時候自動收成一欄，不會硬擠兩欄再溢出。
       ⚠ 要用 minmax(_, 1fr) 而不是 1fr——`1fr` 等同 `minmax(auto, 1fr)`，
       那個 auto 會讓軌道不能縮到內容的最小寬度以下，input 就會頂破格線。 */
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 6px 10px;
}

.opt-row {
    display: flex;
    align-items: center;
    gap: 4px;
}

.opt-input {
    flex: 1;
    min-width: 0;
    padding: 7px 10px;
    font-size: 14px;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    outline: none;
    background: #fff;
}

.opt-del {
    /* 不參與收縮，否則窄的時候會被輸入框擠到寬度 0、按不到 */
    flex: 0 0 auto;
    width: 20px;
    border: 0;
    background: none;
    color: #9e9e9e;
    cursor: pointer;
    font-size: 15px;
}

.opt-new {
    grid-column: 1 / -1;
    min-width: 0;
    padding: 7px 10px;
    font-size: 14px;
    border: 1px dashed #90caf9;
    border-radius: 4px;
    outline: none;
    background: #fff;
}
</style>
